import styled from "styled-components";
import { Link } from "react-router-dom";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

const StyledTableWrapper = styled.div`
  overflow-x: auto;
  width: 100%;
`;

const StyledTable = styled.table<{ $minWidth?: string }>`
  width: 100%;
  border-collapse: collapse;
  min-width: ${({ $minWidth }) => $minWidth || "1000px"};
`;

const StyledTableHeader = styled.thead``;

const StyledTableHeaderCell = styled.th<{ $align?: "left" | "center" | "right" }>`
  padding: ${spacing.x2}px;
  text-align: ${({ $align }) => $align || "left"};
  font-weight: 600;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  border-bottom: 1px solid ${colors.border};
`;

const StyledTableBody = styled.tbody``;

const StyledTableRow = styled.tr<{ $clickable?: boolean; $active?: boolean }>`
  border-bottom: 1px solid ${colors.border};
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  background-color: ${({ $active }) =>
    $active ? colors.backgroundDark : "transparent"};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${({ $clickable, $active }) =>
      $clickable || $active ? colors.backgroundDark : "transparent"};
  }
`;

const RowLink = styled(Link)`
  display: contents;
  text-decoration: none;
  color: inherit;
`;

const StyledTableCell = styled.td<{ $align?: "left" | "center" | "right"; $noWrap?: boolean }>`
  padding: ${spacing.x2}px;
  font-size: ${fontSizes.regular}px;
  color: ${colors.text};
  white-space: ${({ $noWrap }) => ($noWrap ? "nowrap" : "normal")};
  text-align: ${({ $align }) => $align || "left"};
`;

type TableProps = {
  minWidth?: string;
  children: React.ReactNode;
};

type TableHeaderCellProps = {
  align?: "left" | "center" | "right";
  children: React.ReactNode;
};

type TableRowProps = {
  clickable?: boolean;
  active?: boolean;
  onClick?: () => void;
  to?: string;
  children: React.ReactNode;
};

type TableCellProps = {
  align?: "left" | "center" | "right";
  noWrap?: boolean;
  children: React.ReactNode;
};

export const Table = ({ minWidth, children }: TableProps) => {
  return (
    <StyledTableWrapper>
      <StyledTable $minWidth={minWidth}>{children}</StyledTable>
    </StyledTableWrapper>
  );
};

export const TableHeader = ({ children }: { children: React.ReactNode }) => {
  return <StyledTableHeader>{children}</StyledTableHeader>;
};

export const TableHeaderCell = ({ align, children }: TableHeaderCellProps) => {
  return <StyledTableHeaderCell $align={align}>{children}</StyledTableHeaderCell>;
};

export const TableBody = ({ children }: { children: React.ReactNode }) => {
  return <StyledTableBody>{children}</StyledTableBody>;
};

export const TableRow = ({
  clickable,
  active,
  onClick,
  to,
  children,
}: TableRowProps) => {
  const isClickable = clickable || !!to;

  return (
    <StyledTableRow
      $clickable={isClickable}
      $active={active}
      onClick={to ? undefined : onClick}
    >
      {to ? <RowLink to={to}>{children}</RowLink> : children}
    </StyledTableRow>
  );
};

export const TableCell = ({ align, noWrap, children }: TableCellProps) => {
  return (
    <StyledTableCell $align={align} $noWrap={noWrap}>
      {children}
    </StyledTableCell>
  );
};
